import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Breadcrumbs, Checkbox, CircularProgress, Fab, Grid, IconButton, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DropzoneArea } from 'mui-file-dropzone';
import { v4 } from 'uuid';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { MdiIcon, useSnackbar } from '@components';
import { mdiContentSave } from '@mdi/js';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
const CrmPessoasAddView = ({ $state, CrmService }) => {
    const [nome, setNome] = useState('');
    const [pessoa, setPessoa] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [image, setImage] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const { showSnackbar } = useSnackbar();
    // classificação: valores possíveis e escolhidos
    const [servicosInteresse, setServicosInteresse] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);
    const [areasFormacao, setAreasFormacao] = useState([]);
    const [classificacao, setClassificacao] = useState({
        servicosInteresse: [],
        departamentos: [],
        areasFormacao: []
    });
    // redes sociais
    // Parse initial social media links from JSON if available
    const [socialMediaLinks, setSocialMediaLinks] = useState({});
    const fetchData = async () => {
        setIsLoading(false);
        try {
            const [getPessoa, servicos, deps, areas] = await Promise.all([
                CrmService.findPessoaById($state.params.id),
                CrmService.getServicosInteresse(),
                CrmService.getDepartamentosContacto(),
                CrmService.getAreasFormacao()
            ]);
            setServicosInteresse(servicos);
            setDepartamentos(deps);
            setAreasFormacao(areas);
            setClassificacao({
                servicosInteresse: getPessoa.servicosInteresse ?? [],
                departamentos: getPessoa.departamentos ?? [],
                areasFormacao: getPessoa.areasFormacao ?? []
            });
            // as relações gravam-se à parte (setClassificacaoContacto), não no patch do contacto
            delete getPessoa.servicosInteresse;
            delete getPessoa.departamentos;
            delete getPessoa.areasFormacao;
            setPessoa(getPessoa);
            if (getPessoa.userImage) {
                let imageUrl = `/api/Upload/crm/download/${getPessoa.userImage}`;
                setImage(imageUrl); // Define a imagem existente
            }
            const parsedLinks = JSON.parse(getPessoa.redesSociais || '{}');
            setSocialMediaLinks(parsedLinks);
            setNome(getPessoa.refContactoPrimavera + ' - ' + getPessoa.nome);
            if (getPessoa.dataNascimento) {
                getPessoa.dataNascimento = moment(getPessoa.dataNascimento);
            }
            formik.setValues(getPessoa);
        }
        catch (e) {
            console.log(e);
        }
    };
    useEffect(() => {
        if (isLoading) {
            fetchData();
        }
    }, [$state.params.id, isLoading]);
    const socialMediaIcons = [
        { src: '/images/crm_facebook.png', alt: 'Facebook', key: 'facebook' },
        { src: '/images/crm_linkedin.png', alt: 'LinkedIn', key: 'linkedin' },
        { src: '/images/crm_twitter.png', alt: 'Twitter', key: 'twitter' },
        { src: '/images/crm_instagram.png', alt: 'Instagram', key: 'instagram' },
        { src: '/images/crm_youtube.png', alt: 'YouTube', key: 'youtube' }
    ];
    const handleSocialMediaChange = (key, value) => {
        setSocialMediaLinks(prev => {
            const updatedLinks = { ...prev };
            if (!value.trim()) {
                delete updatedLinks[key]; // Remove a rede social se o valor for vazio
            }
            else {
                updatedLinks[key] = value; // Atualiza ou adiciona o valor da rede social
            }
            return updatedLinks;
        });
    };
    const validationSchema = yup.object().shape({
        nome: yup.string().required('O campo é obrigatório').min(2, 'O nome deve ter pelo menos 2 caracteres').max(255, 'O nome deve ter no máximo 255 caracteres'),
        email: yup
            .string()
            .required('O campo é obrigatório')
            .email('Introduza um email válido')
            .test('exists', 'O email introduzido já se encontra registado', async (value) => {
            if (!value || !pessoa) {
                return true;
            }
            if (value.includes(pessoa.email)) {
                return true;
            }
            const d = await CrmService.existsInContacto('email', value);
            return d.count === 0;
        })
    });
    const formik = useFormik({
        initialValues: {},
        enableReinitialize: true,
        validationSchema: validationSchema,
        validateOnMount: false,
        onSubmit: async (values) => {
            //console.log(values);
            values.redesSociais = JSON.stringify(socialMediaLinks);
            submit(values);
        }
    });
    const handleImage = async (values) => {
        const formData = new FormData();
        if (newImage.file) {
            const newFileName = values.userImage ? values.userImage : newImage.file.name;
            formData.append('file', newImage.file, newFileName);
            // Se a imagem for igual, não realiza upload
            if (newImage.dummy) {
                //console.log(values);
                return values;
            }
            else {
                // Atualiza o valor da imagem no objeto `values`
                values.userImage = newFileName;
                try {
                    // Upload da nova imagem
                    const response = await fetch('/api/Upload/crm/upload', {
                        method: 'POST',
                        body: formData
                    });
                    if (response.ok) {
                        const content = await response.json();
                        return content.result;
                    }
                    else {
                        //console.log(response);
                        console.error('Erro ao adicionar imagem');
                    }
                }
                catch (error) {
                    console.error('Error uploading file:', error);
                }
            }
        }
        else {
            return values;
        }
    };
    const submit = async (newValues, refresh) => {
        if (newImage !== null) {
            handleImage(newValues);
        }
        if (newValues.redesSociais === '{}') {
            newValues.redesSociais = null;
        }
        try {
            const p = await CrmService.patchContacto($state.params.id, newValues);
            await CrmService.setClassificacaoContacto($state.params.id, {
                servicosInteresse: classificacao.servicosInteresse.map(r => r.id),
                departamentos: classificacao.departamentos.map(r => r.id),
                areasFormacao: classificacao.areasFormacao.map(r => r.id)
            });
            showSnackbar('Contacto atualizado com sucesso');
            if (refresh) {
                setIsLoading(true);
                setPessoa(null);
            }
            else {
                $state.go('app.crm.pessoas.details', { id: p.id });
            }
        }
        catch (error) {
            showSnackbar('Erro ao atualizar contacto');
        }
    };
    return (<Box sx={{ mt: 2, pl: 1, pr: 1 }}>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link underline='none' color='inherit'>
          CRM
        </Link>
        <Link underline='hover' color='text.primary' href={$state.href('app.crm.pessoas.list')}>
          Contactos
        </Link>

        <Link underline='hover' color='text.primary' aria-current='page'>
          Editar
        </Link>
      </Breadcrumbs>

      {isLoading ? (<Box sx={{ display: 'flex', justifyContent: 'center', height: '2vh' }}>
          <CircularProgress size={60} color='primary'/>
        </Box>) : (<Box>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mt: 2 }}>
              <Typography variant='h6' sx={{ color: 'black', mb: 1 }}>
                Informação base
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <TextField fullWidth name='nome' required label='Nome' color='info' size='small' value={formik.values.nome} onChange={formik.handleChange} onBlur={formik.handleBlur} error={Boolean(formik.errors.nome)} helperText={formik.touched.nome && formik.errors.nome} InputLabelProps={{
                shrink: Boolean(formik.values.nome)
            }}/>
                    <TextField fullWidth required name='email' label='Email' color='info' size='small' value={formik.values.email} error={Boolean(formik.errors.email)} helperText={formik.touched.email && formik.errors.email} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.email)
            }}/>

                    <TextField fullWidth name='contactoTelefonico' name='contactoTelefonico' label='Contacto Telefónico' color='info' size='small' value={formik.values.contactoTelefonico} error={Boolean(formik.errors.contactoTelefonico)} helperText={formik.touched.contactoTelefonico && formik.errors.contactoTelefonico} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.contactoTelefonico)
            }}/>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Stack spacing={2}>
                    <TextField fullWidth name='nif' label='NIF' color='info' size='small' value={formik.values.nif} error={Boolean(formik.errors.nif)} helperText={formik.touched.nif && formik.errors.nif} disabled InputLabelProps={{
                shrink: Boolean(formik.values.nif)
            }}/>

                    <TextField fullWidth name='profissao' name='profissao' label='Profissão' color='info' size='small' value={formik.values.profissao} error={Boolean(formik.errors.profissao)} helperText={formik.touched.profissao && formik.errors.profissao} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.profissao)
            }}/>

                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker value={formik.values.dataNascimento} onChange={value => {
                formik.setFieldValue('dataNascimento', value); // Atualiza o campo no Formik
            }} slotProps={{
                textField: {
                    size: 'small',
                    fullWidth: true,
                    label: 'Data de nascimento',
                    helperText: formik.touched.dataNascimento && formik.errors.dataNascimento,
                    error: Boolean(formik.touched.dataNascimento && formik.errors.dataNascimento)
                }
            }}/>
                    </LocalizationProvider>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box>
                    <DropzoneArea acceptedFiles={['image/jpeg', 'image/png']} initialFiles={image ? [image] : []} // Usa a imagem carregada da pessoa ou deixa vazio
         onChange={files => {
                if (pessoa?.userImage !== files[0]?.name) {
                    if (files.length > 0) {
                        let t = files.pop();
                        // Usa o mesmo nome da imagem antiga, se existir
                        let fileName = pessoa.userImage ? pessoa.userImage : `${v4()}.${t.name.split('.').pop()}`;
                        let f = new File([t], fileName, { type: t.type });
                        setNewImage({ file: f, dummy: false });
                        setImage(URL.createObjectURL(f));
                        formik.setFieldValue('userImage', fileName);
                    }
                    else {
                        setNewImage(null);
                        setImage(null);
                        formik.setFieldValue('userImage', null);
                    }
                }
            }} showFileNames={false} showAlerts={false} showPreviewsInDropzone={true} 
        //useChipsForPreview={true}
        dropzoneText='Arraste e solte a imagem aqui ou clique' filesLimit={1}/>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField fullWidth multiline maxRows={4} name='observacoes' name='observacoes' label='Observações' color='info' size='small' value={formik.values.observacoes} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.observacoes)
            }}/>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline minRows={2} maxRows={4} name='interessesPessoais' label='Interesses Pessoais' color='info' size='small' value={formik.values.interessesPessoais} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.interessesPessoais)
            }}/>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField fullWidth multiline minRows={2} maxRows={4} name='interessesProfissionais' label='Interesses Profissionais' color='info' size='small' value={formik.values.interessesProfissionais} onChange={formik.handleChange} onBlur={formik.handleBlur} InputLabelProps={{
                shrink: Boolean(formik.values.interessesProfissionais)
            }}/>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction='row' justifyContent='flex-start' alignItems='center' spacing={2}>
                    <Typography sx={{ color: 'black' }}> Bloqueado </Typography>
                    <Checkbox defaultChecked={formik.values.blocked === 1 ? true : false} // Checkbox marcado se o valor for 1
         disabled/>
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant='h6' sx={{ color: 'black', mb: 2 }}>
                Classificação
              </Typography>

              <Grid container spacing={2}>
                {[
                ['servicosInteresse', 'Serviços de interesse', servicosInteresse],
                ['departamentos', 'Departamentos', departamentos],
                ['areasFormacao', 'Áreas de formação', areasFormacao]
            ].map(([chave, label, opcoes]) => (<Grid item xs={12} md={4} key={chave}>
                    <Autocomplete multiple disableCloseOnSelect size='small' options={opcoes} value={classificacao[chave]} getOptionLabel={r => r.designacao} isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(_, value) => setClassificacao(prev => ({ ...prev, [chave]: value }))} renderOption={(props, option, { selected }) => (<li {...props} key={option.id}>
                          <Checkbox size='small' checked={selected} sx={{ mr: 1, p: 0 }}/>
                          {option.designacao}
                        </li>)} renderInput={params => <TextField {...params} label={label} color='info'/>}/>
                  </Grid>))}
              </Grid>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant='h6' sx={{ color: 'black', mb: 2 }}>
                Redes sociais
              </Typography>

              <Grid container spacing={2}>
                {socialMediaIcons.map((icon, index) => (<Grid item xs={12} sm={6} md={4} key={index} display='flex' alignItems='center'>
                    <Box component='img' src={icon.src} alt={icon.alt} sx={{
                    maxWidth: '22px',
                    maxHeight: '22px',
                    width: 'auto',
                    height: 'auto'
                }}/>
                    <TextField label={`Insira o link da página do ${icon.alt}`} variant='outlined' fullWidth size='small' sx={{ ml: 2 }} value={socialMediaLinks[icon.key] || ''} onChange={e => handleSocialMediaChange(icon.key, e.target.value)}/>
                  </Grid>))}
              </Grid>
            </Box>
          </form>
          <Tooltip title={'Guardar Dados'} placement='top'>
            <Fab aria-label='add' color='error' sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000
            }}>
              <IconButton onClick={() => formik.handleSubmit()}>
                <MdiIcon path={mdiContentSave} sx={{ color: '#ffffff' }}/>
              </IconButton>
            </Fab>
          </Tooltip>
        </Box>)}
    </Box>);
};
export default CrmPessoasAddView;
